/* Deep-link target for failure recovery (`/post-kiosk-failure?token=…&reason=…`). */

import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { JourneyShell } from './JourneyShell.js';
import { Card } from '../../shared/ui/Card.js';
import { RefundRequestCta } from '../checkout/RefundRequestCta.js';
import { useResolvePostKioskToken } from '../checkout/checkoutApi.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { resolvePostAuthFallbackPath } from '../../navigation/navigationEngine.js';
import { usePublishHierarchicalBackFlow } from '../../shared/context/HierarchicalBackFlowContext.js';
import { toCustomerFacingErrorMessage } from '../../shared/errors/toCustomerFacingErrorMessage.js';

export function PostKioskFailureRoute(): JSX.Element {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const shopFallback = resolvePostAuthFallbackPath(tenantCode);
  const [params] = useSearchParams();
  const reason = params.get('reason') ?? 'UNKNOWN';
  const token = params.get('token');
  const reasonKey = ['EXPIRED', 'CANCELLED', 'GATEWAY_ERROR', 'NETWORK_ERROR'].includes(reason)
    ? reason
    : 'UNKNOWN';

  const resolved = useResolvePostKioskToken(token);
  usePublishHierarchicalBackFlow({
    flowKind: 'checkout',
    flowStep: `post-kiosk-failure-${reasonKey.toLowerCase()}`,
    paymentActive: false,
    stepBackTo: shopFallback,
    onStepBack: null,
  });
  const paymentId = resolved.data?.paymentId ?? null;
  const refundEligible =
    resolved.data?.refundEligible !== undefined
      ? resolved.data.refundEligible
      : token === null && reasonKey !== 'CANCELLED';

  return (
    <JourneyShell
      title={t('postKioskFailure.title')}
      context="POST_KIOSK_PRODUCT"
      backTo={shopFallback}
      backLabel={t('checkout.success.backToShop')}
    >
      <Card padded>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[var(--color-on-surface-muted)]">
            {t(`postKioskFailure.reason.${reasonKey}`)}
          </p>
          {resolved.isLoading && token !== null ? (
            <p className="text-sm text-[var(--color-on-surface-muted)]">
              {t('checkout.completePending')}
            </p>
          ) : null}
          {resolved.isError ? (
            <p className="text-sm text-[var(--color-danger)]" role="alert">
              {toCustomerFacingErrorMessage(resolved.error, {
                fallback: t('errors.payment.title'),
              })}
            </p>
          ) : null}
          {paymentId !== null ? (
            <RefundRequestCta paymentId={paymentId} refundEligible={refundEligible} />
          ) : null}
        </div>
      </Card>
    </JourneyShell>
  );
}
